/* eslint-disable no-param-reassign */

import MarkdownIt from 'markdown-it';
import { createStyleMixin, draggableMixin, componentNameValidationMixin } from '@descope-ui/common/components-mixins';
import { compose } from '@descope-ui/common/utils';
import { disableRules } from './consts';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { decodeHTML } from './helpers';
import { getComponentName, injectStyle, observeChildren } from '@descope-ui/common/components-helpers';

export const componentName = getComponentName('enriched-text');

class EnrichedText extends createBaseClass({ componentName, baseSelector: ':host > div' }) {
  #origLinkRenderer;

  #origEmRenderer;

  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
    <div class="content"></div>
    `;

    injectStyle(
      `
      :host {
        line-height: 1em;
        word-break: break-word;
      }
      :host > slot {
        width: 100%;
        display: inline-block;
      }
      *, *:last-child {
        margin: 0;
      }
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        margin-bottom: 1em;
      }
      a {
        cursor: pointer;
      }
      blockquote {
        padding: 0 2em;
      }
      u {
        text-decoration: underline
      }
      s {
        color: currentColor;
      }
        `,
      this
    );

    this.#initProcessor();

    observeChildren(this, this.#parseChildren.bind(this));

    // Stop propagation on link clicks to prevent parent handlers from firing
    this.contentNode?.addEventListener('click', (e) => {
      if (e.target instanceof HTMLAnchorElement) {
        e.stopPropagation();
      }
    });
  }

  static get observedAttributes() {
    return ['readonly', 'link-target-blank'];
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (newValue !== oldValue) {
      if (attrName === 'readonly') {
        this.onReadOnlyChange(newValue === 'true');
      }

      if (attrName === 'link-target-blank') {
        this.#initProcessor();
      }
    }
  }

  // We're overriding the rule for em with single underscore to perform as underline. (_underline_)
  customUnderlineRenderer() {
    this.processor.renderer.rules.em_open = (tokens, idx, options, env, self) => {
      if (tokens[idx].markup === '_') tokens[idx].tag = 'u';
      return this.#origEmRenderer(tokens, idx, options, env, self);
    };
    this.processor.renderer.rules.em_close = (tokens, idx, options, env, self) => {
      if (tokens[idx].markup === '_') tokens[idx].tag = 'u';
      return this.#origEmRenderer(tokens, idx, options, env, self);
    };
  }

  #customizeLinkRenderer() {
    if (this.linkTargetBlank) {
      this.processor.renderer.rules.link_open = (tokens, idx, options, env, self) => {
        // Add a new `target` attribute, or replace the value of the existing one.
        tokens[idx].attrSet('target', '_blank');
        // Pass the token to the default renderer.
        return this.#origLinkRenderer(tokens, idx, options, env, self);
      };
    } else {
      this.processor.renderer.rules.link_open = this.#origLinkRenderer;
    }
  }

  #disableCustomRules() {
    if (!this.processor) {
      return;
    }
    this.processor.disable(disableRules);
  }

  #updateProcessorRules() {
    this.#disableCustomRules();
  }

  #storeOrigRenderers() {
    const defaultLinkRenderer = (tokens, idx, options, _, self) =>
      self.renderToken(tokens, idx, options);
    this.#origLinkRenderer = this.processor.renderer.rules.link_open || defaultLinkRenderer;

    const defaultStrongRenderer = (tokens, idx, options, _, self) =>
      self.renderToken(tokens, idx, options);
    this.#origEmRenderer = this.processor.renderer.rules.em_open || defaultStrongRenderer;
  }

  #initProcessor() {
    this.processor = new MarkdownIt('commonmark', { html: true });
    this.#storeOrigRenderers();
    this.#updateProcessorRules();
    this.#customizeLinkRenderer();
    this.customUnderlineRenderer();
  }

  get linkTargetBlank() {
    return this.getAttribute('link-target-blank') === 'true';
  }

  get contentNode() {
    return this.shadowRoot.querySelector('.content');
  }

  #parseChildren() {
    if (!this.processor) {
      return;
    }

    let html = decodeHTML(this.innerHTML);

    if (!html?.trim() && this.isConnected) {
      this.setAttribute('empty', 'true');
    } else {
      this.removeAttribute('empty');
    }

    try {
      const tokens = this.processor.parse(html, { references: undefined });
      html = this.processor.renderer.render(tokens, { html: true, breaks: true });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Not parsing invalid markdown token');
    }

    this.contentNode.innerHTML = html;
    this.contentNode.firstChild?.setAttribute('part', 'content');
  }

  onReadOnlyChange(isReadOnly) {
    if (isReadOnly) {
      this.contentNode.setAttribute('inert', isReadOnly);
    } else {
      this.contentNode.removeAttribute('inert');
    }
  }
}

export const EnrichedTextClass = compose(
  createStyleMixin({
    mappings: {
      hostWidth: { selector: () => ':host', property: 'width' },
      hostDisplay: { selector: () => ':host', property: 'display', fallback: 'inline-block' },
      hostDirection: { selector: () => ':host', property: 'direction' },
      fontSize: {},
      fontFamily: {},
      fontWeight: {},
      fontWeightBold: [
        { selector: () => ':host strong', property: 'font-weight' },
        { selector: () => ':host b', property: 'font-weight' },
      ],
      textColor: { property: 'color' },
      textLineHeight: { property: 'line-height' },
      textAlign: {},
      linkColor: { selector: 'a', property: 'color' },
      linkTextDecoration: { selector: 'a', property: 'text-decoration' },
      linkHoverTextDecoration: { selector: 'a:hover', property: 'text-decoration' },
      minHeight: {},
      minWidth: {},
    },
  }),
  createStyleMixin({ componentNameOverride: getComponentName('link') }),
  createStyleMixin({ componentNameOverride: getComponentName('text') }),
  draggableMixin,
  componentNameValidationMixin
)(EnrichedText);
